import {
    AfterViewInit,
    Directive,
    ElementRef,
    Input,
    NgZone,
    OnDestroy,
    inject,
} from '@angular/core';

/**
 * Priority-plus overflow for the chart toolbar.
 *
 * Applied to `.chart-tools-inline`. It shows as many `.chart-tool-btn` icons as
 * fit on a single row and collapses the rest behind the trailing
 * `.chart-tools-overflow-trigger` (the 3-dot menu), instead of letting the row
 * wrap to a second line. Purely presentational: it toggles `display` on the
 * buttons it hides and on the trigger - it never removes them from the DOM, so
 * every action stays reachable (hidden ones live in the `#chartToolsMenu`).
 *
 * **Only enable it on a fixed-width toolbar** (the tracker card sets
 * `.chart-tools-inline` to `width: 100%`). Pass `false` for content-sized
 * toolbars: there `.chart-tools-container` is `width: fit-content`, so hiding a
 * button shrinks the container, which makes the next pass hide another - a
 * feedback loop that collapses the whole toolbar into the trigger. When
 * disabled the directive is a strict no-op: every button stays visible and the
 * trigger stays hidden, leaving the toolbar exactly as it renders without it.
 */
@Directive({
    selector: '[chartToolbarOverflow]',
    standalone: true,
})
export class ChartToolbarOverflowDirective implements AfterViewInit, OnDestroy {
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly zone = inject(NgZone);

    /** Off by default; only fixed-width toolbars (tracker cards) opt in. */
    private enabled = false;

    @Input('chartToolbarOverflow') set overflowEnabled(value: boolean | '') {
        // `chartToolbarOverflow` with no value binds `''`; treat that as on.
        this.enabled = value === '' || value === true;
        this.schedule();
    }

    private resizeObserver?: ResizeObserver;
    private mutationObserver?: MutationObserver;
    private frame = 0;

    ngAfterViewInit(): void {
        // Layout measurement must not run inside Angular's zone - it would kick
        // off a change-detection cycle on every resize tick.
        this.zone.runOutsideAngular(() => {
            this.resizeObserver = new ResizeObserver(() => this.schedule());
            this.resizeObserver.observe(this.host.nativeElement);

            // Recompute when the visible button set changes (chart type toggle,
            // context flags, etc. add/remove `.chart-tool-btn` children).
            this.mutationObserver = new MutationObserver(() => this.schedule());
            this.mutationObserver.observe(this.host.nativeElement, {
                childList: true,
            });

            this.schedule();
        });
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
        this.mutationObserver?.disconnect();
        if (this.frame) {
            cancelAnimationFrame(this.frame);
        }
    }

    /** Debounce bursts of resize/mutation callbacks into one measurement. */
    private schedule(): void {
        if (this.frame) {
            cancelAnimationFrame(this.frame);
        }
        this.frame = requestAnimationFrame(() => {
            this.frame = 0;
            this.recompute();
        });
    }

    private recompute(): void {
        const host = this.host.nativeElement;
        const trigger = host.querySelector<HTMLElement>(
            '.chart-tools-overflow-trigger',
        );
        const buttons = Array.from(host.children).filter(
            (el): el is HTMLElement =>
                el instanceof HTMLElement &&
                el.classList.contains('chart-tool-btn') &&
                el !== trigger,
        );
        // Start from a clean slate: every button visible, trigger hidden. This
        // is also the final state for disabled (content-sized) toolbars.
        buttons.forEach(btn => this.show(btn));
        this.hide(trigger);

        if (!this.enabled || !buttons.length) {
            return;
        }

        // Fit test by the SUM of the visible children's intrinsic widths, not
        // `scrollWidth`. `justify-content: space-between` spreads items to the
        // container edges, which inflates `scrollWidth` past `clientWidth` by a
        // rounding margin even when the icons comfortably fit - a false positive
        // that would collapse the whole toolbar. Summing rendered widths is
        // independent of how the row is justified.
        const gap = parseFloat(getComputedStyle(host).columnGap) || 0;
        const avail = host.clientWidth + 1;
        const visibleWidth = (): number => {
            const vis = Array.from(host.children).filter(
                (el): el is HTMLElement =>
                    el instanceof HTMLElement &&
                    getComputedStyle(el).display !== 'none',
            );
            return (
                vis.reduce(
                    (sum, el) => sum + el.getBoundingClientRect().width,
                    0,
                ) + gap * Math.max(0, vis.length - 1)
            );
        };

        // Everything fits on one row - nothing to collapse.
        if (visibleWidth() <= avail) {
            return;
        }

        // Reveal the trigger (it now occupies space) and hide buttons from the
        // end until the visible set plus the trigger fit on one row.
        this.show(trigger);
        for (let i = buttons.length - 1; i >= 0; i--) {
            if (visibleWidth() <= avail) {
                break;
            }
            this.hide(buttons[i]);
        }
    }

    // The chart tool buttons carry `display: flex !important` (Material icon
    // button), so a plain inline `display:none` is ignored - the hide/show must
    // set/clear the property at `!important` priority to win the cascade.
    private hide(el: HTMLElement | null): void {
        el?.style.setProperty('display', 'none', 'important');
    }

    private show(el: HTMLElement | null): void {
        el?.style.removeProperty('display');
    }
}
